import React, { FC, useState, useRef, useEffect, useMemo } from "react";
import { RouterLink } from "../../../components/RouterLink";
import { Header } from "../../../components/Header";
import { Tabs, Button, message, Dropdown, Menu } from "antd";
import { Container } from "../../../components/Container";
import styles from "./styles.module.less";
import { createForm, FormInstance } from "../../../hooks/createForm";
import { VerifyCodeLoginForm } from "../../../components/forms/login/VerifyCodeLoginForm";
import { AuthingTabs } from "../../../components/AuthingTabs/index";
import { SocialLogin } from "../../../components/SocialLogin/index";
import { QrCodeForm } from "../../../components/forms/login/QrCodeForm/index";
import { User } from "authing-js-sdk";
import {
    getDisplayName,
    isWeComeBrowser,
    getSocialAuthUrl,
    isLarkBrowser,
    getDingtalkAppAuthUrl,
    isDingtalkBrowser,
} from "../../../utils";
import { Protocol, SocialConnectionProvider } from "../../../config/index";
import querystring from "query-string";
import { PasswordLoginForm } from "../../../components/forms/login/PasswordLoginForm/index";
import { useTitle } from "../../../hooks/useTitle";
import { useNavigateWithSearch } from "../../../hooks/useNavigateWithSearch";
import {
    MFALoginForm,
    MFAErrorData,
} from "../../../components/forms/login/MFALoginForm";
import classnames from "classnames";
import { noviceEventBus, isRegister } from "../../../components/Guidance/utils";
import { LdapLoginForm } from "../../../components/forms/login/LdapLoginForm";
import { ADLoginForm } from "../../../components/forms/login/ADLoginForm";
import { useTranslation } from "react-i18next";
import { ToggleLang } from "@/components/ToggleLang";
import {
    ApplicationMfaType,
    ErrorCodes,
    getLoginTabsMap,
    ScreenSize,
} from "@/constants/enum";
import { DownOutlined } from "@ant-design/icons";
import { useGotoRegisterComplete } from "../../Register/registerUser";
import { useScreenSize } from "@/hooks/useScreenSize";
import { i18n } from "@/locales";
import { authingTranslation } from "@/hooks/useAuthingTranslation";
import { LoadingComponent } from "@/components/Loading";

const { TabPane } = Tabs;

const getDefaultTab = (tab: string, config: any) => {
    const tabs = config.loginTabs.list;
    const currTab = tabs.find((t: string) => t === tab) || tabs[0];
    let defaultTab = currTab;
    // 判断是不是包含了 qrcode 有的话就去 qrcodeTabsSettings 里面找
    if (
        currTab &&
        currTab.indexOf("qrcode") !== -1 &&
        config.qrcodeTabsSettings &&
        Object.keys(config.qrcodeTabsSettings).includes(currTab)
    ) {
        const currQRTabs = config.qrcodeTabsSettings[currTab];
        // 再去里面便利找 isDefault 的
        const r = currQRTabs.find((each: any) => each.isDefault);
        if (r) {
            defaultTab = `${currTab}:${r?.id}`;
        }
    }
    return defaultTab;
};

const getQrcodeTab = (tab: string, config: any) => {
    const tabs = config.loginTabs.list;
    const currTab = tabs.find((t: string) => t === tab) || tabs[0];
    // 判断是不是包含了 qrcode 有的话就去 qrcodeTabsSettings 里面找
    if (
        currTab &&
        currTab.indexOf("qrcode") !== -1 &&
        config.qrcodeTabsSettings &&
        Object.keys(config.qrcodeTabsSettings).includes(currTab)
    ) {
        const currQRTabs = config.qrcodeTabsSettings[currTab];
        return currQRTabs;
    }
    return undefined;
};

const isNeedComplete = (user: any) => {
    const needCompleteFields = window.__config__.extendsFields;
    const userFields = needCompleteFields.filter(
        (item) => item.type === "internal"
    );
    const udvs = needCompleteFields.filter((item) => item.type !== "internal");
    let need = false;
    for (const f of userFields) {
        // gender 特例
        if (f.name === "gender" && user['gender'] === "U") {
            need = true;
            break;
        }
        if (!user[f.name as any] || user[f.name] === "") {
            need = true;
            break;
        }
    }
    if (!need) {
        // 判断 udv
        const { customData } = user;
        if (customData) {
            for (const f of udvs) {
                if (!customData[f.name] || customData[f.name] === "") {
                    need = true;
                    break;
                }
            }
        }
    }
    return need;
};

const MFA_LABEL = {
    [ApplicationMfaType.EMAIL]: i18n.t("common.emailVerification"),
    [ApplicationMfaType.SMS]: i18n.t("common.SMSVerification"),
    [ApplicationMfaType.OTP]: authingTranslation("common.OTPVerification"),
    [ApplicationMfaType.FACE]: i18n.t("common.faceVerification"),
    [ApplicationMfaType.NINGTON]: i18n.t("common.nington"),
};

const getQueryMFAToken = () => {
    const queryVal = querystring.parse(window.location.search).mfa_token;
    if (typeof queryVal === "string") {
        return queryVal;
    }
    if (Array.isArray(queryVal)) {
        return queryVal.filter((item) => item)[0] || "";
    }
    return "";
};

export const LoginV1: FC = () => {
    const { t } = useTranslation();

    useTitle(t("common.login"));

    type FormRef = React.RefObject<FormInstance>;

    const config = window.__config__;
    const initMFAToken = getQueryMFAToken();

    const formsRef = useRef<{ [key: string]: FormRef }>({});
    const [activeTabKey, setActiveTabKey] = useState("");
    const [loading, setLoading] = useState(false);
    const [fields, setFields] = useState<{ [key: string]: string }>({});
    const navigate = useNavigateWithSearch();
    const [currTimer, setCurrTimer] = useState<any>();
    const [isMFA, setIsMFA] = useState(Boolean(initMFAToken));
    const [MFAInfo, setMFAInfo] = useState<MFAErrorData>();
    const [mfaType, setMfaType] = useState(ApplicationMfaType.OTP);

    const [screenSize] = useScreenSize();

    const mfaFormRef = createForm();

    const [needComplete, gotoRegisterComplete] = useGotoRegisterComplete();

    const [formHidden, setFormHidden] = useState({
        action: "",
        token: "",
    });
    const submitBtn = useRef<any>();
    const agreements = useMemo(() => config?.agreements?.filter(
        (agree) =>
            agree.lang === i18n.language &&
            (config.ssoPageComponentDisplay.autoRegisterThenLoginHintInfo || !!agree?.availableAt)
    )
        , [i18n.language, config]);
    useEffect(() => {
        const query = querystring.parse(window.location.search);
        const path = query["goto"];
        if (path) {
            navigate(path as string);
        }
        const tab = query["tab"] as string;
        setActiveTabKey(getDefaultTab(tab || config.loginTabs.default, config));
    }, [config, navigate]);

    const submit = async () => {
        if (loading) return;
        // 提交表单
        if (isMFA) {
            mfaFormRef.current?.submit();
            return;
        }

        formsRef.current[activeTabKey]?.current?.submit();
    };

    const onSuccess = async (user: User, hideLoading?: boolean) => {
        if (hideLoading) {
            setLoading(false);
        }

        if (!user) {
            return;
        }
        await window.__authing__.setCurrentUser(user);

        message.success(
            t("common.loginSuccessWelcome", {
                name: getDisplayName(user),
            })
        );
        // 登录注册时间相同，表示是自动注册的，进入补充注册信息)
        if (isRegister(user) && needComplete && isNeedComplete(user)) {
            await gotoRegisterComplete();
            return;
        }
        // 需要判断是不是需要进入注册信息补全，根据信息是否有
        if (
            needComplete &&
            window.__config__?.complateFiledsPlace?.includes("login") &&
            isNeedComplete(user)
        ) {
            console.log('isNeedComplete(user):%j', isNeedComplete(user));
            await gotoRegisterComplete();
            return;
        }

        setTimeout(() => {
            // TODO: 这段逻辑与 src/utils/loginSuccess.tsx 的 loginSuccessRedirect 一致，谁敢替换一下
            if (config.protocol === Protocol.OIDC) {
                const query = querystring.parse(window.location.search);
                setFormHidden({
                    action: `/interaction/oidc/${query["uuid"]}/login`,
                    token: user.token ?? "",
                });
                submitBtn.current.click();
                return;
            }

            if (config.protocol === Protocol.SAML) {
                setFormHidden({
                    action: `/interaction/saml-idp/login`,
                    token: user.token ?? "",
                });
                submitBtn.current.click();
                return;
            }

            if (config.protocol === Protocol.CAS) {
                setFormHidden({
                    action: `/interaction/cas-idp/login`,
                    token: user.token ?? "",
                });
                submitBtn.current.click();
                return;
            }

            if (config.protocol === Protocol.OAUTH) {
                setFormHidden({
                    action: `/interaction/oauth/login`,
                    token: user.token ?? "",
                });
                setFields(querystring.parse(window.location.search) as any);
                submitBtn.current.click();
                return;
            }

            // protocol=none 时，直接带着用户信息跳转过去
            const redirectUrl = `${config.redirectUris && config.redirectUris[0]
                }/?data=${encodeURIComponent(JSON.stringify(user))}`;
            window.location.href = redirectUrl;
        }, 500);
    };

    const onFail = (error: any) => {
        setLoading(false);
        // 个人版 MFA
        if (error.code === ErrorCodes.OTP_MFA_CODE) {
            setIsMFA(true);
            setMfaType(ApplicationMfaType.OTP);
            setMFAInfo({ ...error.data, errorCode: error.code });
            return;
        }

        // 企业版 MFA
        if (error.code === ErrorCodes.MSG_MFA_CODE) {
            const mfaData = error.data as MFAErrorData;

            const orderedMsgMfa = mfaData?.applicationMfa
                ?.filter((item) => item.status)
                ?.sort((a, b) => a.sort - b.sort);

            setIsMFA(true);
            setMFAInfo({
                ...mfaData,
                applicationMfa: orderedMsgMfa,
                errorCode: error.code,
            });

            setMfaType(orderedMsgMfa![0].mfaPolicy);

            return;
        }
        console.log("登录失败:\n", error);
    };

    const formFactories: {
        [key: string]: (ref: FormRef, idpId?: string) => JSX.Element;
    } = {
        password: (ref) => (
            <PasswordLoginForm
                ref={ref}
                onSubmit={() => setLoading(true)}
                onSuccess={onSuccess}
                onFail={onFail}
                onFinishFailed={() => setLoading(false)}
                withCustomData={true}
                agreements={agreements}

            />
        ),
        ldap: (ref) => (
            <LdapLoginForm
                ref={ref}
                onSubmit={() => setLoading(true)}
                onSuccess={onSuccess}
                onFail={onFail}
                onFinishFailed={() => setLoading(false)}
                agreements={agreements}

            />
        ),
        ad: (ref) => (
            <ADLoginForm
                ref={ref}
                onSubmit={() => setLoading(true)}
                onSuccess={onSuccess}
                onFail={onFail}
                onFinishFailed={() => setLoading(false)}
                agreements={agreements}

            />
        ),
        "phone-code": (ref) => (
            <VerifyCodeLoginForm
                ref={ref}
                onSubmit={() => setLoading(true)}
                onSuccess={onSuccess}
                onFail={onFail}
                onFinishFailed={() => setLoading(false)}
                agreements={agreements}

            />
        ),
        "wechat-miniprogram-qrcode": (ref, idpId?) => (
            <QrCodeForm
                key="wechat-miniprogram-qrcode"
                ref={ref}
                onSuccess={onSuccess}
                onFail={onFail}
                type="wechat-miniprogram-qrcode"
                idpId={idpId}
                setTimer={setCurrTimer}
            />
        ),
        "wechatmp-qrcode": (ref, idpId?) => (
            <QrCodeForm
                key="wechatmp-qrcode"
                ref={ref}
                onSuccess={onSuccess}
                onFail={onFail}
                type="wechatmp-qrcode"
                idpId={idpId}
                setTimer={setCurrTimer}
            />
        ),
        MFA: (ref) => (
            <MFALoginForm
                ref={ref}
                MFAInfo={MFAInfo}
                MFAType={mfaType}
                onSubmit={() => setLoading(true)}
                onSuccess={onSuccess}
                onFail={onFail}
            />
        ),
        "app-qrcode": (ref, idpId?) => (
            <QrCodeForm
                key="app-qrcode"
                ref={ref}
                onSuccess={onSuccess}
                onFail={onFail}
                type="app-qrcode"
                idpId={idpId}
                setTimer={setCurrTimer}
            />
        ),
    };

    const tabs = config.loginTabs.list
        // 过滤掉不支持的方式
        .filter((tab) => {
            // 如果有冒号，去掉冒号过滤
            return Object.keys(formFactories).includes(tab);
        });

    const tabPanes: any[] = [];
    tabs.forEach((tab) => {
        let title;
        let form;
        const ref = (formsRef.current[tab] = createForm());

        const qrTab = getQrcodeTab(tab, config);
        if (qrTab) {
            qrTab.forEach(
                (qr: { title: string; id: string; isDefault?: boolean }) => {
                    title = qr.title;
                    form = formFactories[tab](ref, qr.id);
                    tabPanes.push(
                        <TabPane tab={title} key={`${tab}:${qr.id}`}>
                            {form}
                        </TabPane>
                    );
                }
            );
        } else {
            title = getLoginTabsMap(window.__config__)[tab];
            form = formFactories[tab](ref);
            tabPanes.push(
                <TabPane tab={title} key={tab}>
                    {form}
                </TabPane>
            );
        }
    });

    const noForm =
        !config.ssoPageComponentDisplay.loginMethodNav || tabPanes.length === 0;
    const showGotoRegisterButton =
        config.ssoPageComponentDisplay.registerBtn &&
        !["ad", "ldap"].includes(activeTabKey);

    const loginElements = !noForm &&
        activeTabKey.split(":")[0] !== "wechat-miniprogram-qrcode" &&
        activeTabKey.split(":")[0] !== "app-qrcode" &&
        activeTabKey.split(":")[0] !== "wechatmp-qrcode" && (
            <>
                {config.ssoPageComponentDisplay.loginBtn && (
                    <Button
                        data-novice-focus="7"
                        data-novice-action="7"
                        loading={loading}
                        className={classnames(styles.registerBtn, "authing-login-btn")}
                        block
                        size="large"
                        type="primary"
                        onClick={submit}
                    >
                        {t("common.login")}
                    </Button>
                )}

                {/* 桌面端样式 */}
                <div
                    className={classnames(
                        styles.gotoRegister,
                        "authing-goto-register-container",
                        "hide-in-mobile"
                    )}
                >
                    {config.ssoPageComponentDisplay.forgetPasswordBtn &&
                        !["ad", "ldap"].includes(activeTabKey) && (
                            <RouterLink
                                to="/reset-password"
                                className="__authing-forget-password"
                            >
                                {t("common.hasForgotPwd")}
                            </RouterLink>
                        )}
                    {showGotoRegisterButton && (
                        <span>
                            {t("common.noAccYet")}
                            <RouterLink
                                onClick={() => noviceEventBus.emit("next")}
                                data-novice-focus="5"
                                data-novice-action="5"
                                to="/register"
                            >
                                {t("common.registerImmediate")}
                            </RouterLink>
                        </span>
                    )}
                </div>
                {config.enableSubAccount && (
                    <div
                        className="authing-guard-form-actions authing-sub-acc-login-btn hide-in-mobile"
                        style={{
                            marginTop: showGotoRegisterButton ? 20 : 0,
                            textAlign: "right",
                            color: "#396aff",
                            fontFamily: "PingFangSC-Medium",
                        }}
                    >
                        <div className="authing-guard-tip-btn-comb">
                            <RouterLink to="/sub-account">
                                {t("login.subAccLogin")}
                            </RouterLink>
                        </div>
                    </div>
                )}

                {/* 移动端样式 */}
                <div
                    className={classnames(
                        styles.mobileLoginActions,
                        "hide-in-desktop",
                        "authing-mobile-login-actions"
                    )}
                >
                    {showGotoRegisterButton && (
                        <RouterLink
                            onClick={() => noviceEventBus.emit("next")}
                            to="/register"
                        >
                            {t("common.registerImmediate")}
                        </RouterLink>
                    )}

                    {config.enableSubAccount && (
                        <>
                            <div
                                className={classnames(
                                    styles.actionSplit,
                                    "authing-login-action-split"
                                )}
                            ></div>
                            <RouterLink to="/sub-account">
                                {t("login.subAccLogin")}
                            </RouterLink>
                        </>
                    )}
                    <>
                        {showGotoRegisterButton && (
                            <div
                                className={classnames(
                                    styles.actionSplit,
                                    "authing-login-action-split"
                                )}
                            ></div>
                        )}
                        <div
                            className={classnames(
                                styles.problem,
                                "authing-guard-feedback-btn"
                            )}
                            onClick={() => {
                                navigate("/feedback");
                            }}
                        >
                            {t("login.feedback")}
                        </div>
                    </>
                    {config.ssoPageComponentDisplay.forgetPasswordBtn &&
                        !["ad", "ldap"].includes(activeTabKey) && (
                            <>
                                <div
                                    className={classnames(
                                        styles.actionSplit,
                                        "authing-login-action-split"
                                    )}
                                ></div>
                                <RouterLink
                                    to="/reset-password"
                                    className="__authing-forget-password"
                                >
                                    {t("login.forgetPwd")}
                                </RouterLink>
                            </>
                        )}
                </div>

                <div style={{ marginBottom: 26 }}></div>

                <div style={{ flex: 1 }}></div>
            </>
        );

    const selectMFAType = (
        <>
            {(MFAInfo?.applicationMfa?.length || 0) > 1 && (
                <div
                    className={classnames(
                        styles.otherVerify,
                        "authing-use-other-mfa-btn"
                    )}
                >
                    <Dropdown
                        overlay={
                            <Menu>
                                {MFAInfo?.applicationMfa
                                    ?.filter((i) => i.mfaPolicy !== mfaType)
                                    .map((item) => (
                                        <Menu.Item
                                            onClick={() => setMfaType(item.mfaPolicy)}
                                            key={item.mfaPolicy}
                                        >
                                            {MFA_LABEL[item.mfaPolicy]}
                                        </Menu.Item>
                                    ))}
                            </Menu>
                        }
                    >
                        <span
                            style={{
                                cursor: "pointer",
                                color: "#999999",
                            }}
                        >
                            {t("login.otherVerifyWay")} <DownOutlined />
                        </span>
                    </Dropdown>
                </div>
            )}
        </>
    );

    const MFAOptElements = (
        <>
            <Button
                loading={loading}
                className={classnames(
                    styles.mfaLoginBtn,
                    styles.registerBtn,
                    "authing-mfa-login-btn"
                )}
                block
                size="large"
                type="primary"
                onClick={submit}
            >
                {MFAInfo?.totpMfaEnabled ? t("common.sure") : t("common.TotpBindBtn")}
            </Button>
            {(MFAInfo?.totpMfaEnabled ||
                MFAInfo?.errorCode === ErrorCodes.OTP_MFA_CODE) && (
                    <span
                        className={classnames(
                            styles.mfaForgetTips,
                            "authing-use-mfa-recover-code-btn"
                        )}
                    >
                        {t("common.hasLooseSaftyCode")}
                        <RouterLink
                            to={{
                                pathname: "/reset-mfa?",
                                search: querystring.stringify(
                                    Object.assign(querystring.parse(window.location.search), {
                                        mfa_token: MFAInfo?.mfaToken,
                                    })
                                ),
                            }}
                            className="__authing-forget-password"
                        >
                            {t("common.useRecoverCode")}
                        </RouterLink>
                    </span>
                )}

            {selectMFAType}
        </>
    );

    const MFAMsgElements = (
        <>
            <Button
                loading={loading}
                className={classnames(
                    styles.mfaLoginBtn,
                    styles.registerBtn,
                    "authing-mfa-login-btn"
                )}
                block
                size="large"
                type="primary"
                onClick={submit}
            >
                {t("common.sure")}
            </Button>

            {selectMFAType}
        </>
    );

    const MFAFaceElements = <>{selectMFAType}</>;

    const MFAElementMap = {
        [ApplicationMfaType.OTP]: MFAOptElements,
        [ApplicationMfaType.NINGTON]: MFAOptElements,
        [ApplicationMfaType.SMS]: MFAMsgElements,
        [ApplicationMfaType.EMAIL]: MFAMsgElements,
        [ApplicationMfaType.FACE]: MFAFaceElements,
    };

    const loginStyles = useMemo(
        () => (isMFA ? "" : "authing-login-main"),
        [isMFA]
    );

    // 企业微信内部应用在企业微信内打开直接登录
    const wecomeInnerAppLogin = config?.socialConnections?.find(
        (item) =>
            item.provider === SocialConnectionProvider.WECHATWORK_CORP_QRCONNECT
    );
    if (wecomeInnerAppLogin && isWeComeBrowser()) {
        window.location.href = getSocialAuthUrl(wecomeInnerAppLogin);
        return <LoadingComponent />;
    }

    // 飞书企业自建应用在飞书内打开直接登录
    const larkInnerAppLogin = config?.socialConnections?.find(
        (item) => item.provider === SocialConnectionProvider.LARK_INTERNAL
    );
    if (larkInnerAppLogin && isLarkBrowser()) {
        window.location.href = getSocialAuthUrl(larkInnerAppLogin);
        return <LoadingComponent />;
    }
    const getDescription = () => {
        if (config.welcomeMessage) {
            if (i18n.language === "zh-CN") {
                return config.welcomeMessage["zh-CN"];
            } else {
                return config.welcomeMessage["en-US"];
            }
        } else {
            return undefined;
        }
    };

    // 钉钉浏览器免登录
    const dingtalkInnerAppLogin = config?.socialConnections?.find(
        (item) => item.provider === SocialConnectionProvider.DINGTALK
    );
    if (
        dingtalkInnerAppLogin &&
        dingtalkInnerAppLogin.corpId &&
        isDingtalkBrowser()
    ) {
        window.location.href = getDingtalkAppAuthUrl(dingtalkInnerAppLogin, dingtalkInnerAppLogin.identifier);
        return <LoadingComponent />;
    }

    return (
        <Container minHeight={noForm ? "auto" : undefined} className={loginStyles}>
            <Header
                title={config.name}
                logo={config.logo}
                description={getDescription()}
            />
            {config.ssoPageComponentDisplay.loginMethodNav && !isMFA && (
                <AuthingTabs
                    centered
                    activeKey={activeTabKey}
                    onChange={(val: any) => {
                        clearInterval(currTimer);
                        setActiveTabKey(val);
                    }}
                >
                    {tabPanes}
                </AuthingTabs>
            )}
            {isMFA && formFactories.MFA(mfaFormRef)}
            {isMFA ? MFAElementMap[mfaType] : loginElements}
            {!isMFA && (
                <SocialLogin noForm={noForm} onFail={onFail} onSuccess={onSuccess} />
            )}
            <form
                action={formHidden.action}
                method="post"
                style={{ display: "none" }}
            >
                {Object.entries(fields).map(([k, v]) => (
                    <input key={k} type="text" name={k} value={v} readOnly />
                ))}
                <input type="text" name="token" value={formHidden.token} readOnly />
                <button ref={submitBtn} type="submit" />
            </form>

            <div className={classnames(styles.layoutFlex)}>
                {screenSize !== ScreenSize.Mobile && <ToggleLang />}
                {isMFA && (
                    <div
                        className={classnames(
                            styles.useAntherAcc,
                            "authing-use-other-accounts"
                        )}
                        onClick={() => {
                            setIsMFA(false);
                        }}
                        style={{
                            textAlign: "center",
                        }}
                    >
                        {t("login.otherAccLogin")}
                    </div>
                )}
                <div
                    className={classnames(
                        styles.problem,
                        "authing-guard-feedback-btn",
                        "hide-in-mobile"
                    )}
                    onClick={() => {
                        navigate("/feedback");
                    }}
                    style={{
                        textAlign: "right",
                    }}
                >
                    {t("common.quesitons")}
                </div>
            </div>
        </Container>
    );
};
